/**
 * PieChart Component
 * Reusable pie/donut chart with labels and percentages
 */

"use client";

import {
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import * as React from "react";
import { Cell, Label, Pie, PieChart as RechartsPieChart } from "recharts";
import { ChartWrapper } from "./ChartWrapper";
import { BaseChartData, PieChartProps } from "./types";
import { calculateTotal, formatPercentage } from "./utils";

export const PieChart = ({
  data,
  config,
  dataKey,
  nameKey,
  title,
  description,
  footer,
  className,
  containerClassName,
  height = 350,
  showTooltip = true,
  showLegend = true,
  tooltipConfig,
  legendConfig,
  innerRadius = 0,
  outerRadius,
  showLabels = true,
  labelType = "percentage",
  centerLabel,
  accessibilityLayer = true,
  metricValue,
  metricChange,
  showMetricLayout = false,
  headerActions,
}: PieChartProps) => {
  // Calculate total for percentage
  const total = React.useMemo(
    () => calculateTotal(data, String(dataKey)),
    [data, dataKey],
  );

  // Custom label renderer
  const renderLabel = (entry: BaseChartData) => {
    if (!showLabels) return null;

    const value = entry[dataKey] as number;

    switch (labelType) {
      case "percentage":
        return formatPercentage(value, total);
      case "value":
        return String(value);
      case "name":
        return String(entry[nameKey]);
      default:
        return null;
    }
  };

  const effectiveOuterRadius =
    outerRadius ||
    (typeof innerRadius === "number" && innerRadius > 0 ? "80%" : "90%");

  const chartContent = (
    <RechartsPieChart accessibilityLayer={accessibilityLayer}>
      {showTooltip && (
        <ChartTooltip
          cursor={false}
          content={<ChartTooltipContent hideLabel />}
        />
      )}

      <Pie
        data={data}
        dataKey={dataKey}
        nameKey={nameKey}
        innerRadius={innerRadius}
        outerRadius={effectiveOuterRadius}
        label={showLabels ? renderLabel : false}
        labelLine={showLabels && innerRadius === 0}
      >
        {data.map((entry: BaseChartData, index: number) => {
          const entryName = String(entry[nameKey]);

          // Use color from config, fallback to dynamic chart color if not defined
          const configColor = config[entryName]?.color;
          const fallbackChartNumber = (index % 5) + 1;
          const chartVariable =
            configColor || `var(--chart-${fallbackChartNumber})`;
          return <Cell key={`cell-${index}`} fill={chartVariable} />;
        })}

        {/* Center label for donut charts */}
        {typeof innerRadius === "number" && innerRadius > 0 && centerLabel && (
          <Label
            value={centerLabel}
            position="center"
            className="fill-foreground text-2xl font-bold"
          />
        )}
      </Pie>

      {showLegend && (
        <ChartLegend
          content={<ChartLegendContent nameKey={String(nameKey)} />}
          className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
        />
      )}
    </RechartsPieChart>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      footer={footer}
      className={className}
      containerClassName={containerClassName || "mx-auto aspect-square"}
      config={config}
      height={height}
      metricValue={metricValue}
      metricChange={metricChange}
      showMetricLayout={showMetricLayout}
      headerActions={headerActions}
    >
      {chartContent}
    </ChartWrapper>
  );
};

PieChart.displayName = "PieChart";

export default PieChart;
